// Where a step is done, as somewhere to go: a CourageMFG screen opens in the app, a Business Central page or report
// opens in BC. A step names its screen in words ("Issues", "Sales Return Order"), so the names are matched here:
// CourageMFG's from the app catalog (suite.ts), so a new app links without a change here, and BC's from the standard
// object IDs. A name nobody knows simply doesn't link. No DOM and no config: the link itself is built by the caller.
import type { Step, SystemKind } from "@couragemfg/api/processes-model";
import { SUITE } from "../suite";

export type ScreenTarget = { kind: "app"; to: string } | { kind: "bc"; page: number } | { kind: "bc-report"; report: number };

const norm = (s: string) => String(s ?? "").toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, " ").trim();

/** Names maps use for CourageMFG screens that aren't an app's or a page's own name. */
const APP_ALIASES: Record<string, string> = {
  "planning worksheet": "/planning", "warehouse picks": "/wms", "warehouse app": "/wms", "order planner": "/planning/orders",
  "quotes": "/opportunities", "my work": "/my-work", "process maps and sops": "/processes", "item setup": "/item-setups",
};

/** Page names too general to mean one screen ("Board" could be any board). */
const GENERIC = new Set(["home", "board", "list", "month", "settings", "report", "schedule", "queue", "dashboard", "dashboards", "import", "people", "topics", "templates", "all open", "new", "segments"]);

/** CourageMFG: every app by name, every page whose name is its own (a page name two routes share, like Home, isn't
 *  used), and every app by its short name. */
function appScreens(): Map<string, string> {
  const out = new Map<string, string>(), pages = new Map<string, Set<string>>();
  for (const d of SUITE) for (const m of d.modules) {
    if (m.path) out.set(norm(m.name), m.path);
    for (const p of m.pages ?? []) if (p.to) { const k = norm(p.name); pages.set(k, (pages.get(k) ?? new Set()).add(p.to)); }
  }
  for (const d of SUITE) for (const p of d.nav ?? []) if (p.to) { const k = norm(p.name); pages.set(k, (pages.get(k) ?? new Set()).add(p.to)); }
  for (const [k, to] of pages) if (to.size === 1 && !out.has(k) && !GENERIC.has(k)) out.set(k, [...to][0]);
  // An app's short name works too: NCR, CAPA, ECO, SCAR.
  for (const d of SUITE) for (const m of d.modules) if (m.path && !out.has(norm(m.id))) out.set(norm(m.id), m.path);
  for (const [k, to] of Object.entries(APP_ALIASES)) if (!out.has(k)) out.set(k, to);
  return out;
}

/** Business Central: standard page IDs for the pages maps name (cards and documents in the singular, lists in the plural). */
const BC_PAGES: Record<string, number> = {
  "sales quote": 41, "sales order": 42, "sales invoice": 43, "sales credit memo": 44, "sales return order": 6630,
  "customer card": 21, "customer ledger entries": 25, "cash receipt journal": 255,
  "purchase quote": 49, "purchase order": 50, "purchase invoice": 51, "purchase credit memo": 52, "purchase return order": 6640, "blanket purchase order": 509,
  "vendor card": 26, "vendor bank account card": 425, "payment journal": 256,
  "item card": 30, "item journal": 40, "item reclassification journal": 393, "phys inventory journal": 392, "item ledger entries": 38,
  "item availability by location": 492, "item tracing": 6520, "lot no information card": 6505,
  "transfer order": 5740, "warehouse receipt": 5768, "warehouse shipment": 7335, "warehouse pick": 5779, "warehouse put away": 5770,
  "production bom": 99000786, "production bom version": 99000809, "routing": 99000766,
  "planning worksheet": 99000852, "requisition worksheet": 291, "subcontracting worksheet": 99000886, "demand forecasts": 99000921,
  "firm planned prod order": 99000829, "released production order": 99000831, "production journal": 5510,
  "output journal": 99000823, "consumption journal": 99000846, "production order statistics": 99000816,
  "general journal": 39, "bank acc reconciliation": 379, "general ledger setup": 118, "accounting periods": 100,
  "fixed asset card": 5600, "reminders": 436, "user card": 9807, "chart of accounts": 16,
};
/** Business Central reports maps name. */
const BC_REPORTS: Record<string, number> = {
  "aged accounts receivable": 120, "adjust cost item entries": 795, "post inventory cost to g l": 1002, "trial balance": 6, "work center load": 99000783,
};
/** The BC pages in the app catalog (Sales Orders, Planning Worksheet…), for names the table above doesn't have. */
function bcCatalog(): Map<string, number> {
  const out = new Map<string, number>();
  for (const d of SUITE) for (const m of d.modules) for (const p of m.pages ?? []) if (p.bc && !out.has(norm(p.name))) out.set(norm(p.name), p.bc);
  return out;
}

let cache: { app: Map<string, string>; bc: Map<string, number> } | null = null;
const tables = () => (cache ??= { app: appScreens(), bc: bcCatalog() });

/** Where to open a step's screen, or null when it's manual or the name isn't one CourageMFG or BC knows. */
export function screenTarget(s: Pick<Step, "system" | "screen">): ScreenTarget | null {
  const k = norm(s.screen);
  if (!k) return null;
  if (s.system === "courage") { const to = tables().app.get(k); return to ? { kind: "app", to } : null; }
  if (s.system === "bc") {
    if (BC_PAGES[k]) return { kind: "bc", page: BC_PAGES[k] };
    if (BC_REPORTS[k]) return { kind: "bc-report", report: BC_REPORTS[k] };
    const page = tables().bc.get(k);
    return page ? { kind: "bc", page } : null;
  }
  return null;
}

/** Screen names that link, for suggestions as someone types: CourageMFG apps, or the BC pages and reports above. */
export function screenNames(system: SystemKind): string[] {
  if (system === "courage") {
    const names = new Set<string>();
    for (const d of SUITE) for (const m of d.modules) if (m.path) names.add(m.name);
    return [...names].sort((a, b) => a.localeCompare(b));
  }
  if (system === "bc") return BC_NAMES;
  return [];
}
const BC_NAMES = ["Sales Quote", "Sales Order", "Sales Invoice", "Sales Credit Memo", "Sales Return Order", "Customer Card", "Customer Ledger Entries", "Cash Receipt Journal",
  "Purchase Quote", "Purchase Order", "Purchase Invoice", "Purchase Credit Memo", "Purchase Return Order", "Blanket Purchase Order", "Vendor Card", "Vendor Bank Account Card",
  "Payment Journal", "Item Card", "Item Journal", "Item Reclassification Journal", "Phys. Inventory Journal", "Item Ledger Entries", "Item Availability by Location", "Item Tracing",
  "Lot No. Information Card", "Transfer Order", "Warehouse Receipt", "Warehouse Shipment", "Warehouse Pick", "Warehouse Put-away", "Production BOM", "Production BOM Version",
  "Routing", "Planning Worksheet", "Requisition Worksheet", "Subcontracting Worksheet", "Demand Forecasts", "Firm Planned Prod. Order", "Released Production Order",
  "Production Journal", "Output Journal", "Consumption Journal", "Production Order Statistics", "General Journal", "Bank Acc. Reconciliation", "General Ledger Setup",
  "Accounting Periods", "Fixed Asset Card", "Reminders", "User Card", "Chart of Accounts", "Aged Accounts Receivable", "Adjust Cost - Item Entries",
  "Post Inventory Cost to G/L", "Trial Balance", "Work Center Load"].sort((a, b) => a.localeCompare(b));
